"use client";

import React from "react";
import { Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import ServiceQuoteSection from "@/components/service/ServiceQuoteSection";
import ServiceFormSuccess from "@/components/service/ServiceFormSuccess";
import { inputClass, textareaClass, selectClass, labelClass, submitClass, formCardClass } from "@/components/service/formStyles";
const SERVICES = ["New Roofs", "Flat Roofs", "Roof Cleaning", "Fascias/Soffits/Gutters", "Chimneys", "Repointing", "Block Paving", "Resin Bound", "Tarmac", "Turfing & Artificial Grass", "Patios", "Fencing", "Jet Washing"];
const PROPERTY_TYPES = ["Residential", "Commercial", "Industrial"];
function FencingQuoteInnerForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <ServiceFormSuccess title="Quote Request Sent" message={successMessage || "Thank you for your enquiry! We have received your details and will be in touch shortly to discuss your project."} onReset={resetForm} resetLabel="Request Another Quote" />;
  }
  return <Form onSubmit={handleSubmit} className={formCardClass}>
      <FormField control={control} name="name" rules={{
      required: "Full name is required",
      minLength: {
        value: 2,
        message: "Name must be at least 2 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className={labelClass}>Full Name</FormLabel>
            <FormControl>
              <Input placeholder="John Doe" className={inputClass} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Email address is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className={labelClass}>Email Address</FormLabel>
            <FormControl>
              <Input type="email" placeholder="john@example.com" className={inputClass} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="phone" rules={{
      required: "Phone number is required",
      minLength: {
        value: 10,
        message: "Please enter a valid phone number"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className={labelClass}>Phone Number</FormLabel>
            <FormControl>
              <Input type="tel" placeholder="07443 802652" className={inputClass} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="postcode" rules={{
      required: "Postcode is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className={labelClass}>Postcode</FormLabel>
            <FormControl>
              <Input placeholder="e.g. SO15 1AA" className={inputClass} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="serviceType" rules={{
      required: "Please select a service"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className={labelClass}>Service Required</FormLabel>
            <FormControl>
              <select {...field} className={selectClass}>
                <option value="">Select a service...</option>
                {SERVICES.map((service, i) => <option key={i} value={service}>
                    {service}
                  </option>)}
              </select>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="propertyType" rules={{
      required: "Please select a property type"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className={labelClass}>Property Type</FormLabel>
            <FormControl>
              <select {...field} className={selectClass}>
                <option value="">Select property type...</option>
                {PROPERTY_TYPES.map((type, i) => <option key={i} value={type}>
                    {type}
                  </option>)}
              </select>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="description" rules={{
      required: "Please provide some details about your project",
      minLength: {
        value: 10,
        message: "Please provide a bit more detail"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className={labelClass}>Project Details</FormLabel>
            <FormControl>
              <textarea placeholder="Please describe what you need help with..." className={textareaClass} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
          <p className="text-destructive text-sm font-medium">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className={submitClass}>
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Sending Request...
          </> : "Get My Free Quote"}
      </Button>
    </Form>;
}
export default function FencingQuoteForm() {
  return <ServiceQuoteSection id="get-a-free-quote" eyebrow="GET A FREE QUOTE" heading="Ready to Secure and Upgrade Your Southampton Property?" intro="Fill out the form below with your project details, and Matt and the Firststop Roofing Ltd team will get back to you promptly with a clear, no-obligation fencing quote in Southampton, Hampshire & surrounding areas." serviceName="Fencing">
      <FormProvider formId="fencing-quote-form" sectionName="FencingQuoteForm" defaultValues={{
      name: "",
      email: "",
      phone: "",
      postcode: "",
      serviceType: "Fencing",
      propertyType: "",
      description: ""
    }} validationRules={{
      name: {
        required: true,
        minLength: 2
      },
      email: {
        required: true,
        pattern: "email"
      },
      phone: {
        required: true
      },
      postcode: {
        required: true
      },
      serviceType: {
        required: true
      },
      propertyType: {
        required: true
      },
      description: {
        required: true,
        minLength: 10
      }
    }} submitText="Get My Free Quote" successMessage="Thank you for your enquiry! We have received your details and will be in touch shortly to discuss your project.">
        <FencingQuoteInnerForm />
      </FormProvider>
    </ServiceQuoteSection>;
}